/**
 * ProductGallery - Extends Slider with draggable thumbnail strip and SKU group switching
 *
 * Data attributes:
 * - [data-product-gallery] - Gallery root (required)
 * - [data-slider-scroll-target] - Scroll container (from Slider)
 * - [data-slider-item] - Individual image figure elements
 * - [data-slider-indicators] - Container for thumbnail indicators
 * - [data-slider-thumb] - Thumbnail button for each image
 * - [data-thumb-viewport] - Scrollable thumbnail viewport
 *
 * @class
 * @extends Slider
 * @example
 * const gallery = new ProductGallery(element);
 * await gallery.switchToVariant(variant);
 */
import Slider from "@theme/slider";

const LOADING_CLASS = "animate-pulse";
const THUMB_SIZE = 56; // w-14 = 56px
const THUMB_GAP = 8; // gap-2 = 8px
const THUMB_STEP = THUMB_SIZE + THUMB_GAP;

export default class ProductGallery extends Slider {
  /**
   * Initialize product gallery with images and indicators.
   * Extends Slider with snap mode enabled.
   *
   * @param {HTMLElement} root - Gallery root element (must have Slider data attributes)
   */
  constructor(root) {
    super(root, { snap: true });
    this.indicatorsContainer = root.querySelector("[data-slider-indicators]");
    this._thumbDragged = false;
    this._dragInited = false;
    this._scrollAbort = null;

    this.initGallery();
    this.container?.scrollTo({ left: 0, behavior: "smooth" });
  }

  /**
   * Initialize gallery UI: radio indicators, labels, and scroll tracking.
   * Dispatches 'gallery:change' event when scroll position changes.
   *
   * @returns {void}
   * @private
   * @fires gallery:change
   */
  initGallery() {
    this.thumbs = Array.from(this.root.querySelectorAll("[data-slider-thumb]"));
    this.thumbViewport = this.root.querySelector("[data-thumb-viewport]");

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener("click", () => {
        if (this._thumbDragged) return;
        document.getElementById(thumb.dataset.target)?.scrollIntoView({
          behavior: "smooth",
          inline: "start",
          block: "nearest",
        });
      });
    });

    // Abort any previous scroll listener before registering a fresh one
    this._scrollAbort?.abort();
    this._scrollAbort = new AbortController();

    this.onScroll(
      (index) => {
        this.updateThumbs(index, true);
        this.root.dispatchEvent(
          new CustomEvent("gallery:change", {
            detail: { item: this.items[index], index },
          }),
        );
      },
      { signal: this._scrollAbort.signal },
    ); // requires Slider.onScroll to forward signal

    if (!this._dragInited) {
      this._buildTrack();
      this._initDrag();
      this._dragInited = true;
    }

    if (this.items.length > 1) {
      this.indicatorsContainer?.classList.replace("hidden", "flex");
    } else {
      this.indicatorsContainer?.classList.replace("flex", "hidden");
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._applyOffset(0);
      });
    });
  }

  _buildTrack() {
    const viewport = this.thumbViewport;
    if (!viewport || viewport.querySelector("[data-thumb-track]")) return;

    const track = document.createElement("div");
    track.setAttribute("data-thumb-track", "");
    track.style.cssText = "display:flex; gap:8px; will-change:transform; user-select:none;";

    while (viewport.firstChild) track.appendChild(viewport.firstChild);
    viewport.appendChild(track);
    this._track = track;
  }

  _initDrag() {
    const viewport = this.thumbViewport;
    if (!viewport) return;

    // Clean up previous drag handlers before adding new ones
    this._dragAbort?.abort();
    this._dragAbort = new AbortController();
    const { signal } = this._dragAbort;

    let isDragging = false;
    let prevPageX = 0;
    let prevScrollLeft = 0;
    let positionDiff = 0;

    const dragStart = (e) => {
      isDragging = true;
      this._thumbDragged = false;
      prevPageX = e.pageX || e.touches?.[0]?.pageX || 0;
      prevScrollLeft = viewport.scrollLeft;
      viewport.style.cursor = "grabbing";
    };

    const dragging = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      positionDiff = (e.pageX || e.touches?.[0]?.pageX || 0) - prevPageX;
      if (Math.abs(positionDiff) > 4) this._thumbDragged = true;
      viewport.scrollLeft = prevScrollLeft - positionDiff;
    };

    const dragStop = () => {
      if (!isDragging) return;
      isDragging = false;
      viewport.style.cursor = "grab";

      // Snap to nearest thumb
      const snapped = Math.round(viewport.scrollLeft / THUMB_STEP) * THUMB_STEP;
      viewport.scrollTo({ left: snapped, behavior: "smooth" });

      setTimeout(() => {
        this._thumbDragged = false;
      }, 50);
    };

    viewport.addEventListener("mousedown", dragStart, { signal });
    viewport.addEventListener("mousemove", dragging, { signal });
    viewport.addEventListener("mouseup", dragStop, { signal });
    viewport.addEventListener("mouseleave", dragStop, { signal });

    viewport.addEventListener("touchstart", dragStart, { passive: true, signal });
    viewport.addEventListener("touchmove", dragging, { passive: false, signal });
    viewport.addEventListener("touchend", dragStop, { signal });
    viewport.addEventListener("touchcancel", dragStop, { signal });

    // Prevent native image drag
    viewport.addEventListener("dragstart", (e) => e.preventDefault(), { signal });
  }

  /** Maximum px the track can scroll right (always positive) */
  _maxOffset() {
    if (!this.thumbViewport) return 0;
    return Math.max(0, this.thumbViewport.scrollWidth - this.thumbViewport.offsetWidth);
  }

  /** Jump to position without animation */
  _applyOffset(pixelsFromStart) {
    if (!this.thumbViewport) return;
    const max = this._maxOffset();
    const target = Math.max(0, Math.min(max, pixelsFromStart));
    this.thumbViewport.scrollLeft = target;
  }

  /** Animate to position */
  _smoothScrollTo(pixelsFromStart) {
    if (!this.thumbViewport) return;
    const max = this._maxOffset();
    const target = Math.max(0, Math.min(max, pixelsFromStart));
    this.thumbViewport.scrollTo({ left: target, behavior: "smooth" });
  }

  /**
   * Update thumbnail active states and optionally scroll the active thumb into view.
   *
   * @param {number} activeIndex - Index of the currently active image
   * @param {boolean} scrollIntoView - Whether to auto-scroll the thumbnail strip
   * @returns {void}
   * @private
   */
  updateThumbs(activeIndex, scrollIntoView = false) {
    this.thumbs.forEach((thumb, i) => {
      const isActive = i === activeIndex;
      thumb.setAttribute("aria-selected", String(isActive));
      thumb.classList.toggle("border-red-700", isActive);
      thumb.classList.toggle("border-transparent", !isActive);
    });

    if (!scrollIntoView || !this.thumbViewport) return;

    const thumb = this.thumbs[activeIndex];
    if (!thumb) return;

    // Measure from the DOM — works regardless of thumb size
    const thumbLeft = thumb.offsetLeft;
    const thumbRight = thumbLeft + thumb.offsetWidth;
    const currentScroll = this.thumbViewport.scrollLeft;
    const viewportWidth = this.thumbViewport.offsetWidth;

    if (thumbLeft < currentScroll) {
      // Thumb is hidden to the left
      this._smoothScrollTo(thumbLeft);
    } else if (thumbRight > currentScroll + viewportWidth) {
      // Thumb is hidden to the right
      this._smoothScrollTo(thumbRight - viewportWidth);
    }
  }

  /**
   * Show image group for a specific SKU by scrolling into view.
   * Groups are organized by [data-slider-group="sku"] containers.
   *
   * @param {string} sku - SKU identifier to find and display
   * @returns {boolean} True if group was found and scrolled, false otherwise
   * @private
   */
  showGroup(sku) {
    const encoded = this._encodeSku(sku);
    const group = this.root.querySelector(`[data-slider-group="${encoded}"]`);
    const firstItem = group?.querySelector("[data-slider-item]");
    if (firstItem) {
      firstItem.scrollIntoView({ behavior: "smooth", inline: "start" });
      return true;
    }
    return false;
  }

  /**
   * Switch gallery to images for a specific variant.
   * First tries to show an existing SKU group; if not found, fetches product data
   * and rebuilds the gallery with matching images for the variant's SKU.
   *
   * @param {Object} variant - Variant object with sku and featured_image
   * @param {string} variant.sku - Unique SKU identifier
   * @param {string} variant.featured_image - Fallback image URL
   * @returns {Promise<boolean>} True if switch succeeded, false on error
   * @throws {Error} Logs error but does not throw; returns false instead
   */
  async switchToVariant(variant) {
    if (!variant) return false;
    const sku = variant.sku?.trim() || null;
    if (sku && this.showGroup(sku)) return true;
    this.container.classList.add(LOADING_CLASS);
    return this.fetchAndRebuildGallery(variant);
  }

  /**
   * Fetch product data and rebuild gallery with images matching the variant's SKU.
   * Filters images by SKU prefix (e.g., "1980121001-182__1.jpg") and falls back
   * to variant.featured_image if no matches found. Includes preloading for CLS prevention.
   *
   * @param {Object} variant - Variant object with sku, featured_image, name, title
   * @returns {Promise<boolean>} True if build succeeded, false on error
   * @private
   * @fires gallery:change (from reinitGallery)
   */
  async fetchAndRebuildGallery(variant) {
    try {
      const handle = this.root.dataset.productHandle || window.location.pathname.split("/").pop();
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error("Failed to fetch product data");

      const { images } = await response.json();
      const sku = variant.sku?.trim() || null;

      const encodedSku = sku ? this._encodeSku(sku) : null;
      let matchingImages = encodedSku ? images.filter((img) => this.getFilename(img).toLowerCase().startsWith(`${encodedSku}__`)) : [];

      if (!matchingImages.length && variant.featured_image) {
        matchingImages = [variant.featured_image];
      }

      await this.rebuildGalleryHTML(matchingImages, sku, variant);
      this.reinitialize();
      return true;
    } catch (error) {
      console.error("Error fetching variant images:", error);
      this.container.classList.remove(LOADING_CLASS);
      return false;
    }
  }

  /**
   * Extract filename from image object or URL string.
   * Strips query parameters and path components.
   *
   * @param {string|Object} img - Image URL or { src: '...' } object
   * @returns {string} Filename without query parameters (e.g., "image.jpg")
   * @private
   */
  getFilename(img) {
    const src = typeof img === "string" ? img : img?.src || "";
    return src.split("/").pop()?.split("?")[0] || "";
  }

  /**
   * Encode a SKU to match Shopify's filename convention.
   * Shopify replaces spaces with _20 in filenames.
   */
  _encodeSku(sku) {
    return sku.toLowerCase().replace(/ /g, "_20");
  }

  /**
   * Get resized Shopify image URL.
   * If image is a Shopify URL, appends ?width parameter for responsive images.
   * For non-Shopify URLs, returns as-is.
   *
   * @param {string|Object} img - Image URL or { src: '...' } object
   * @param {number} [width=800] - Desired image width
   * @returns {string} Resized image URL with width parameter for Shopify images
   * @private
   */
  getResizedSrc(img, width = 800) {
    const src = typeof img === "string" ? img : img?.src || img || "";
    return src.includes("shopify.com") ? `${src.split("?")[0]}?width=${width}` : src;
  }

  /**
   * Rebuild gallery HTML with new images.
   * Preloads the first image to prevent CLS before swapping in the HTML.
   * All images fade from opacity-0 to opacity-100 for smooth transition.
   *
   * @param {Array} images - List of image objects or URLs
   * @param {string} sku - SKU to associate with this group
   * @param {Object} variant - Variant object with name/title for alt text
   * @returns {Promise<void>} Resolves when gallery is fully rendered and images preloaded
   * @private
   */
  rebuildGalleryHTML(images, sku, variant) {
    return new Promise((resolve) => {
      const alt = variant.name || variant.title || "";
      const encodedSku = sku ? this._encodeSku(sku) : sku;
      const html = `<div class="contents" data-slider-group="${encodedSku}">
        ${images
          .map(
            (img, i) => `
          <figure
            id="gallery-img-${i + 1}"
            class="min-w-full snap-start snap-always scroll-mt-0 aspect-square"
            data-slider-item
            data-slider-sku="${sku}"
          >
            <img
              src="${this.getResizedSrc(img)}"
              alt="${this._escapeHtml(alt)}"
              class="w-full h-auto object-cover opacity-0 transition-opacity duration-300"
              loading="${i === 0 ? "eager" : "lazy"}"
              data-gallery-img
            />
          </figure>
        `,
          )
          .join("")}
      </div>`;

      const showContent = () => {
        this.container.innerHTML = html;
        this.container.scrollTo({ left: 0, top: 0, behavior: "auto" });
        this.container.querySelectorAll("[data-gallery-img]").forEach((img) => {
          img.classList.replace("opacity-0", "opacity-100");
        });
        this.rebuildIndicators(images, alt);
        this.container.classList.remove(LOADING_CLASS);
        resolve();
      };

      if (images.length) {
        const preload = new Image();
        preload.src = this.getResizedSrc(images[0]);
        preload.complete ? showContent() : (preload.onload = preload.onerror = showContent);
      } else {
        showContent();
      }
    });
  }

  /**
   * Rebuild thumbnail indicators based on image list.
   * Creates one thumbnail button per image. Hides container if 1 or fewer images.
   *
   * @param {Array} images - List of image objects or URLs
   * @param {string} [alt=''] - Alt text for thumbnail images
   * @returns {void}
   * @private
   */
  rebuildIndicators(images, alt = "") {
    const container = this.indicatorsContainer;
    if (!container || images.length <= 1) {
      container?.classList.remove("flex");
      container?.classList.add("hidden");
      return;
    }

    const viewport = container.querySelector("[data-thumb-viewport]");
    if (!viewport) return;

    // Tear down old track so _buildTrack + _initDrag re-run fresh
    const oldTrack = viewport.querySelector("[data-thumb-track]");
    if (oldTrack) {
      while (oldTrack.firstChild) viewport.insertBefore(oldTrack.firstChild, oldTrack);
      oldTrack.remove();
    }
    this._track = null;
    this._dragInited = false;

    viewport.innerHTML = images
      .map((img, i) => {
        const n = i + 1;
        const isFirst = i === 0;
        return `
        <button
          type="button"
          data-slider-thumb="${n}"
          data-target="gallery-img-${n}"
          class="shrink-0 w-20 md:w-28 aspect-square rounded-md overflow-hidden border-2 transition-all focus:outline-none ${isFirst ? "border-red-700" : "border-transparent"}"
          role="tab"
          aria-label="Go to image ${n}"
          aria-selected="${isFirst}"
        >
          <img src="${this.getResizedSrc(img, 120)}" alt="${this._escapeHtml(alt)}" class="w-full h-full object-cover pointer-events-none" loading="eager" />
        </button>
      `;
      })
      .join("");

    container.classList.remove("hidden");
    container.classList.add("flex");
  }

  /**
   * Reinitialize gallery after DOM rebuild.
   * Recaches slider items and reinitializes gallery UI (indicators, scroll tracking).
   *
   * @returns {void}
   * @private
   */
  destroy() {
    this._scrollAbort?.abort();
    this._scrollAbort = null;
    this._dragAbort?.abort();
    this._dragAbort = null;
    super.destroy();
  }

  reinitialize() {
    this.items = this.container.querySelectorAll("[data-slider-item]");
    this.thumbs = Array.from(this.root.querySelectorAll("[data-slider-thumb]"));
    this.thumbViewport = this.root.querySelector("[data-thumb-viewport]");
    this.initGallery();
    this.updateControls();
  }

  // for some reason importing escapeHtml causes issues, so keeping it local here for now
  _escapeHtml(text) {
    if (typeof text !== 'string') return text;
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

// Auto-initialize all galleries on the page
const initGalleries = () => {
  document.querySelectorAll('[data-product-gallery]').forEach((el) => {
    const gallery = new ProductGallery(el);

    // Wire up variant switching when the variant selector changes
    document.addEventListener('change', async (e) => {
      if (e.target?.name !== 'id') return;
      const handle = el.dataset.productHandle || window.location.pathname.split('/').pop();
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) throw new Error('Failed to fetch product');
        const product = await res.json();
        const variant = product.variants.find((v) => v.id == e.target.value);
        if (variant) gallery.switchToVariant(variant);
      } catch (err) {
        console.error('Error switching gallery variant:', err);
      }
    });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGalleries);
} else {
  initGalleries();
}
